import { motion, useInView } from 'framer-motion';
import { useTranslation } from 'next-i18next';
import React, { useRef } from 'react';

import { ACCENT_BLUE, DARK_BG, EASE_OUT, MUTED_GRAY, NOPING_GREEN, TEXT_LIGHT } from '../constants';
import { ThemedCard } from '../ui-helpers';

export function HyperspaceRouting() {
  const { t } = useTranslation(['components/trial/lp/v4d']);
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });

  const bridges = [
    { name: 'Los Angeles 92', toBridge: '2ms', toServer: '52ms', toBridgeMs: 2, toServerMs: 52, totalMs: 54, color: ACCENT_BLUE },
    { name: 'Chicago 31', toBridge: '48ms', toServer: '1ms', toBridgeMs: 48, toServerMs: 1, totalMs: 49, color: ACCENT_BLUE },
    { name: 'Dallas 47', toBridge: '30ms', toServer: '12ms', toBridgeMs: 30, toServerMs: 12, totalMs: 42, color: NOPING_GREEN },
    { name: 'Iowa 18', toBridge: '38ms', toServer: '5ms', toBridgeMs: 38, toServerMs: 5, totalMs: 43, color: ACCENT_BLUE },
    { name: 'Los Angeles 94', toBridge: '1ms', toServer: '48ms', toBridgeMs: 1, toServerMs: 48, totalMs: 49, color: ACCENT_BLUE },
  ];

  const bestMs = Math.min(...bridges.map((b) => b.totalMs));
  const W = 700;
  const H = 200;
  const userX = 40;
  const mergeX = 540;
  const serverX = 660;
  const centerY = H / 2;
  const bridgeX = 290;
  const laneYs = [22, 58, centerY, H - 58, H - 22];
  const MS_TO_SEC = 0.1;
  const worstTotalSec = Math.max(...bridges.map((b) => b.totalMs)) * MS_TO_SEC;
  const PACKETS_PER_BRIDGE = 5;
  const PACKET_INTERVAL = worstTotalSec / PACKETS_PER_BRIDGE;

  return (
    <div ref={ref} className='w-full'>
      <ThemedCard className='p-3 md:p-5' glow={ACCENT_BLUE}>
        <div className='mb-2 flex items-center justify-between'>
          <span className='text-[10px] font-semibold uppercase tracking-widest md:text-xs' style={{ color: ACCENT_BLUE, fontFamily: 'var(--font-outfit)' }}>
            {t('hyperspace.engine_label')}
          </span>
          <div className='flex items-center gap-2'>
            <motion.div className='size-1.5 rounded-full' style={{ backgroundColor: NOPING_GREEN }} animate={{ opacity: [1, 0.3, 1] }} transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' as const }} />
            <span className='rounded border px-2 py-0.5 text-[10px] font-bold' style={{ color: NOPING_GREEN, borderColor: `${NOPING_GREEN}40`, backgroundColor: `${NOPING_GREEN}08`, fontFamily: 'var(--font-outfit)' }}>
              {t('hyperspace.bridges_active')}
            </span>
          </div>
        </div>

        <div className='overflow-hidden rounded-lg' style={{ backgroundColor: DARK_BG, border: `1px solid ${ACCENT_BLUE}10` }}>
          <svg viewBox={`0 0 ${W} ${H}`} className='w-full' preserveAspectRatio='xMidYMid meet'>
            <defs>
              <filter id='pktGlow'>
                <feGaussianBlur stdDeviation='3' result='b' />
                <feMerge>
                  <feMergeNode in='b' />
                  <feMergeNode in='b' />
                  <feMergeNode in='SourceGraphic' />
                </feMerge>
              </filter>
              <filter id='iconGlow'>
                <feGaussianBlur stdDeviation='2' result='b' />
                <feMerge>
                  <feMergeNode in='b' />
                  <feMergeNode in='SourceGraphic' />
                </feMerge>
              </filter>
            </defs>

            <g>
              <rect x={userX - 12} y={centerY - 12} width='18' height='13' rx='2' fill='none' stroke={TEXT_LIGHT} strokeWidth='1.2' />
              <line x1={userX - 7} y1={centerY + 2.5} x2={userX + 3} y2={centerY + 2.5} stroke={TEXT_LIGHT} strokeWidth='1' />
              <line x1={userX - 2} y1={centerY + 2.5} x2={userX - 2} y2={centerY + 6} stroke={TEXT_LIGHT} strokeWidth='1' />
              <line x1={userX - 6} y1={centerY + 6} x2={userX + 2} y2={centerY + 6} stroke={TEXT_LIGHT} strokeWidth='0.8' />
              <rect x={userX - 20} y={centerY - 7} width='6' height='11' rx='1.5' fill='none' stroke={TEXT_LIGHT} strokeWidth='0.8' />
              <circle cx={userX - 17} cy={centerY + 2} r='0.5' fill={TEXT_LIGHT} />
              <text x={userX - 6} y={centerY + 16} textAnchor='middle' fill={TEXT_LIGHT} fontSize='7' fontFamily='var(--font-outfit)' fontWeight='600'>
                {t('hyperspace.you')}
              </text>
              <text x={userX - 6} y={centerY + 23} textAnchor='middle' fill={MUTED_GRAY} fontSize='5' fontFamily='var(--font-outfit)'>
                Los Angeles
              </text>
            </g>

            <g filter='url(#iconGlow)'>
              <rect x={serverX - 13} y={centerY - 16} width='26' height='32' rx='3' fill={`${NOPING_GREEN}10`} stroke={NOPING_GREEN} strokeWidth='1' />
              <rect x={serverX - 10} y={centerY - 13} width='20' height='7' rx='1' fill={`${NOPING_GREEN}08`} stroke={NOPING_GREEN} strokeWidth='0.5' strokeOpacity='0.5' />
              <rect x={serverX - 10} y={centerY - 4} width='20' height='7' rx='1' fill={`${NOPING_GREEN}08`} stroke={NOPING_GREEN} strokeWidth='0.5' strokeOpacity='0.5' />
              <rect x={serverX - 10} y={centerY + 5} width='20' height='7' rx='1' fill={`${NOPING_GREEN}08`} stroke={NOPING_GREEN} strokeWidth='0.5' strokeOpacity='0.5' />
              <circle cx={serverX - 7} cy={centerY - 9.5} r='1' fill={NOPING_GREEN} opacity='0.9' />
              <circle cx={serverX - 4} cy={centerY - 9.5} r='1' fill={NOPING_GREEN} opacity='0.4' />
              <circle cx={serverX - 7} cy={centerY - 0.5} r='1' fill={ACCENT_BLUE} opacity='0.7' />
              <circle cx={serverX - 4} cy={centerY - 0.5} r='1' fill={ACCENT_BLUE} opacity='0.3' />
              <circle cx={serverX - 7} cy={centerY + 8.5} r='1' fill={NOPING_GREEN} opacity='0.6' />
              <circle cx={serverX - 4} cy={centerY + 8.5} r='1' fill={NOPING_GREEN} opacity='0.3' />
              {[0, 3, 6].map((dy) => (
                <line key={dy} x1={serverX + 2} y1={centerY - 12 + dy} x2={serverX + 8} y2={centerY - 12 + dy} stroke={NOPING_GREEN} strokeWidth='0.3' opacity='0.3' />
              ))}
              <line x1={serverX} y1={centerY - 16} x2={serverX} y2={centerY - 22} stroke={NOPING_GREEN} strokeWidth='0.8' />
              <circle cx={serverX} cy={centerY - 23} r='1.5' fill={NOPING_GREEN} opacity='0.8' />
              <path d={`M${serverX - 4} ${centerY - 26} A 4 4 0 0 1 ${serverX + 4} ${centerY - 26}`} fill='none' stroke={NOPING_GREEN} strokeWidth='0.5' opacity='0.4' />
              <path d={`M${serverX - 6} ${centerY - 28} A 6 6 0 0 1 ${serverX + 6} ${centerY - 28}`} fill='none' stroke={NOPING_GREEN} strokeWidth='0.4' opacity='0.25' />
              <text x={serverX} y={centerY + 22} textAnchor='middle' fill={NOPING_GREEN} fontSize='5.5' fontFamily='var(--font-outfit)' fontWeight='700' letterSpacing='0.3'>
                {t('hyperspace.game_server')}
              </text>
              <text x={serverX} y={centerY + 28} textAnchor='middle' fill={MUTED_GRAY} fontSize='5' fontFamily='var(--font-outfit)'>
                Chicago
              </text>
            </g>

            <g>
              <rect x={mergeX - 8} y={centerY - 10} width='16' height='20' rx='2' fill={`${NOPING_GREEN}12`} stroke={NOPING_GREEN} strokeWidth='0.8' />
              <line x1={mergeX - 6} y1={centerY - 4} x2={mergeX + 6} y2={centerY - 4} stroke={NOPING_GREEN} strokeWidth='0.4' opacity='0.5' />
              <line x1={mergeX - 6} y1={centerY + 2} x2={mergeX + 6} y2={centerY + 2} stroke={NOPING_GREEN} strokeWidth='0.4' opacity='0.5' />
              <circle cx={mergeX - 4} cy={centerY - 7} r='0.8' fill={NOPING_GREEN} />
              <circle cx={mergeX - 4} cy={centerY - 1} r='0.8' fill={NOPING_GREEN} opacity='0.7' />
              <circle cx={mergeX - 4} cy={centerY + 5} r='0.8' fill={NOPING_GREEN} opacity='0.5' />
            </g>

            <line x1={mergeX + 10} y1={centerY} x2={serverX - 16} y2={centerY} stroke={NOPING_GREEN} strokeWidth='1.5' opacity='0.6' />
            <line x1={userX + 8} y1={centerY} x2={userX + 28} y2={centerY} stroke={NOPING_GREEN} strokeWidth='1.5' opacity='0.5' />

            {bridges.map((bridge, i) => {
              const ly = laneYs[i]!;
              const isBest = bridge.totalMs === bestMs;
              const forkX = userX + 28;
              const pathD = `M ${userX + 8} ${centerY} L ${forkX} ${centerY} L ${forkX + 18} ${ly} L ${bridgeX - 18} ${ly}`;
              const pathD2 = `M ${bridgeX + 18} ${ly} L ${mergeX - 28} ${ly} L ${mergeX - 10} ${centerY}`;
              const leg1Dur = Math.max(bridge.toBridgeMs * MS_TO_SEC, 0.45);
              const leg2Dur = Math.max(bridge.toServerMs * MS_TO_SEC, 0.45);

              return (
                <g key={`bridge-${i}`}>
                  <motion.path d={pathD} fill='none' stroke={bridge.color} strokeWidth={isBest ? 1.5 : 0.8} strokeOpacity={isBest ? 0.6 : 0.25} strokeDasharray={isBest ? 'none' : '4 3'} initial={{ pathLength: 0 }} animate={inView ? { pathLength: 1 } : {}} transition={{ duration: 0.7, delay: 0.08 * i, ease: EASE_OUT }} />
                  <motion.path d={pathD2} fill='none' stroke={bridge.color} strokeWidth={isBest ? 1.5 : 0.8} strokeOpacity={isBest ? 0.6 : 0.25} strokeDasharray={isBest ? 'none' : '4 3'} initial={{ pathLength: 0 }} animate={inView ? { pathLength: 1 } : {}} transition={{ duration: 0.5, delay: 0.08 * i + 0.4, ease: EASE_OUT }} />

                  <rect x={bridgeX - 10} y={ly - 7} width='20' height='14' rx='2' fill={`${bridge.color}10`} stroke={bridge.color} strokeWidth='0.6' strokeOpacity='0.5' />
                  <line x1={bridgeX - 7} y1={ly - 1} x2={bridgeX + 7} y2={ly - 1} stroke={bridge.color} strokeWidth='0.35' opacity='0.4' />
                  <line x1={bridgeX - 7} y1={ly + 3} x2={bridgeX + 7} y2={ly + 3} stroke={bridge.color} strokeWidth='0.35' opacity='0.4' />
                  <circle cx={bridgeX - 6} cy={ly - 4} r='0.7' fill={bridge.color} opacity='0.8' />
                  <circle cx={bridgeX - 6} cy={ly + 1} r='0.7' fill={bridge.color} opacity='0.5' />
                  <circle cx={bridgeX - 6} cy={ly + 5} r='0.7' fill={bridge.color} opacity='0.4' />

                  <text x={bridgeX} y={ly - 11} textAnchor='middle' fill={bridge.color} fontSize='5.5' fontFamily='var(--font-outfit)' fontWeight='600' opacity='0.7' letterSpacing='0.3'>
                    {bridge.name.toUpperCase()}
                  </text>

                  <rect x={forkX + 22} y={ly - 5.5} width='24' height='11' rx='5.5' fill={`${bridge.color}15`} stroke={bridge.color} strokeWidth='0.4' strokeOpacity='0.4' />
                  <text x={forkX + 34} y={ly + 2} textAnchor='middle' fill={bridge.color} fontSize='5.5' fontFamily='var(--font-outfit)' fontWeight='700'>
                    {bridge.toBridge.toUpperCase()}
                  </text>

                  <rect x={bridgeX + 24} y={ly - 5.5} width='24' height='11' rx='5.5' fill={`${bridge.color}15`} stroke={bridge.color} strokeWidth='0.4' strokeOpacity='0.4' />
                  <text x={bridgeX + 36} y={ly + 2} textAnchor='middle' fill={bridge.color} fontSize='5.5' fontFamily='var(--font-outfit)' fontWeight='700'>
                    {bridge.toServer.toUpperCase()}
                  </text>

                  <rect x={mergeX - 44} y={ly - 5} width='30' height='10' rx='5' fill={`${bridge.color}12`} stroke={bridge.color} strokeWidth='0.4' strokeOpacity='0.3' />
                  <text x={mergeX - 29} y={ly + 2} textAnchor='middle' fill={bridge.color} fontSize='5' fontFamily='var(--font-outfit)' fontWeight='700'>{`\u03A3 ${bridge.totalMs}MS`}</text>

                  {inView &&
                    Array.from({ length: PACKETS_PER_BRIDGE }).map((_, pIdx) => {
                      const pDelay = 1.5 + pIdx * PACKET_INTERVAL;
                      const fullCycle = PACKETS_PER_BRIDGE * PACKET_INTERVAL + worstTotalSec;
                      return (
                        <g key={`pkt-${i}-${pIdx}`}>
                          <circle r={isBest ? 3.5 : 2.5} fill={bridge.color} filter='url(#pktGlow)'>
                            <animateMotion dur={`${fullCycle}s`} begin={`${pDelay}s`} repeatCount='indefinite' keyTimes={`0;${leg1Dur / fullCycle};1`} keyPoints='0;1;1' path={pathD} />
                          </circle>
                          <circle r={isBest ? 3.5 : 2.5} fill={bridge.color} filter='url(#pktGlow)'>
                            <animateMotion dur={`${fullCycle}s`} begin={`${pDelay + leg1Dur}s`} repeatCount='indefinite' keyTimes={`0;${leg2Dur / fullCycle};1`} keyPoints='0;1;1' path={pathD2} />
                          </circle>
                        </g>
                      );
                    })}
                </g>
              );
            })}
          </svg>
        </div>

        <div className='mt-3 grid grid-cols-3 gap-2'>
          {[
            { value: '5', label: t('hyperspace.stat_bridges'), desc: t('hyperspace.stat_bridges_desc') },
            { value: '5', label: t('hyperspace.stat_hops'), desc: t('hyperspace.stat_hops_desc') },
            { value: '32Q+', label: t('hyperspace.stat_routes'), desc: t('hyperspace.stat_routes_desc') },
          ].map((s, i) => (
            <div key={i} className='rounded-lg py-1.5 text-center' style={{ backgroundColor: `${ACCENT_BLUE}06`, border: `1px solid ${ACCENT_BLUE}10` }}>
              <p className='text-sm font-bold md:text-base' style={{ color: NOPING_GREEN, fontFamily: 'var(--font-bai)' }}>
                {s.value}
              </p>
              <p className='text-[9px] font-medium' style={{ color: TEXT_LIGHT, fontFamily: 'var(--font-outfit)' }}>
                {s.label}
              </p>
              <p className='text-[8px]' style={{ color: MUTED_GRAY }}>
                {s.desc}
              </p>
            </div>
          ))}
        </div>
      </ThemedCard>
    </div>
  );
}
